import React, {
  type ReactNode,
  createContext,
  useCallback,
  useState,
  useMemo,
} from 'react';

import ModalRoot from 'components/core/modal/Root';
import { ModalToastProvider } from 'components/shared/DesignSystem/ModalToast/ModalToastContext';
import { Modal } from 'components/core/modal/constants';
import { usePrevious } from 'javascripts/hooks';

type ComponentName = (typeof Modal)[keyof typeof Modal];
type ComponentProps = Record<string, unknown>;

type ModalState = {
  componentName: string | null;
  props: ComponentProps;
  showModal: (name: ComponentName, props?: ComponentProps) => void;
  hideModal: () => void;
  Modal: typeof Modal;
};

const initialState: ModalState = {
  componentName: null,
  props: {},
  showModal: () => {},
  hideModal: () => {},
  Modal,
};

const ModalContext = createContext(initialState);

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<
    Pick<ModalState, 'componentName' | 'props'>
  >({
    componentName: null,
    props: {},
  });

  const showModal = useCallback((componentName: ComponentName, props = {}) => {
    setState((prevState) => ({
      ...prevState,
      componentName,
      props,
    }));
  }, []);

  const hideModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      componentName: null,
      props: {},
    }));
  }, []);

  const modalProviderValue = useMemo(
    () => ({ ...state, hideModal, showModal, Modal }),
    [state, hideModal, showModal],
  );

  // TODO[Jose]: consider react-router for location once modal is migrated
  const previousLocation = usePrevious(window.location.href.split('#')[0]);
  const currentLocation = window.location.href.split('#')[0];

  /*
   * close modal if the URL changes
   * This is a workaround for the fact that react-router does not
   * unmount the modal when navigating between react pages.
   * Not ideal but should also reconsider this modal architecture.
   */
  if (previousLocation !== currentLocation) {
    if (state.componentName) {
      hideModal();
    }
  }

  return (
    <ModalContext.Provider value={modalProviderValue}>
      <ModalToastProvider>
        <ModalRoot />
        {children}
      </ModalToastProvider>
    </ModalContext.Provider>
  );
};

const ModalConsumer = ModalContext.Consumer;

export { ModalContext, ModalProvider, ModalConsumer };
